/* eslint-disable */
// @ts-nocheck
import { createTextListFromArray } from '../../../../../common/support/utils';
import { DEVICE_TYPES, DEVICE_OSS } from '../journeyConstants';
import { DEVICE_OUTLINES } from '../devices';
import type { PreviewDevice, Devices } from '../../../../types/journeyTypes';

/**
 * Adjusts heights slightly on non 9x16 phones. E.g. iPhone4 ratio
 * is 2x3, iPhone 7+ is 9x16, we take (2/3) / (9/16) and then use that
 * to derive the actual height of distance between the screen and top
 * edge of phone.
 *
 *
 * @param paddingTop {string}
 * @param device {object}
 * @return {number}
 */
function adjustForDimensions(paddingTop: number, device: PreviewDevice): number {
  // iPhone 7+ used to be the basis for all other devices' calculated padding,
  // hence why it's used here
  const basis = DEVICE_OUTLINES.iPhone6_7_8_Plus;
  const currentDimension = device.width / device.height;
  const compareDimension = basis.width / basis.height;
  const ratio = currentDimension / compareDimension;
  return Math.floor(parseInt(paddingTop) * ratio);
}

const getEditorBodyStyle = (device: PreviewDevice) => ({
  backgroundSize: device.backgroundSize + 'px',
  width: device.width + 10 + 'px',
  height: device.height + 20 + 'px',
  paddingTop: adjustForDimensions(device.padding.top, device) + 'px',
  paddingLeft: device.padding.left + 'px',
  paddingBottom: device.padding.bottom + 'px',
  backgroundImage: `url(/static/images/journeys/devices/${device.outlineImage})`,
});

const getTotalHeight = (device: PreviewDevice): number => device.height - (device.padding.bottom || 0);

/**
 * Gets human readable string of devices present
 * (i.e. 'ios' or 'android and ios')
 *
 * @param {Object} devices - from query.devices, object like:
 *        {
            android: ['tablet']
            ios: ['tablet', 'mobile'],
          }
 * @return {string} - grammatically correct string of devices , ex: 'android and ios'
 */
const getDevicesText = (devices: Devices): string => {
  const deviceTypes = Object.keys(devices).reduce((operatingSystems, os) => {
    if (devices[os] && devices[os].length) {
      operatingSystems.push(os);
    }

    return operatingSystems;
  }, []);
  return createTextListFromArray(deviceTypes) || '';
};

/**
* @param {Array} - array of what device types to include:
* Ex 1: ["tablet", "mobile"]
* Ex 2: ["tablet"]

* @return {Object} - Lucene query format for devices:
* Ex 1: {
    includes: ["tablet", "mobile"],
    excludes: []
  }
*
* Ex 2: {
    includes: ["tablet"],
    excludes: ["mobile"]
  }
*/
const sortDeviceTypesByIncludedExcluded = (
  includedDevices?: Array<'mobile' | 'tablet'>
): {
  includes: Array<string | null | undefined>;
  excludes: Array<string | null | undefined>;
} => {
  return DEVICE_TYPES.reduce(
    (acc, deviceType) => {
      // if that device type is to be included in query (if it is
      // in the list of includedDevices passed in)
      if (includedDevices && includedDevices.indexOf(deviceType) > -1) {
        // add to positive list criteria (we want to include these types)
        acc.includes.push(deviceType);
      } else {
        // otherwise add to negative list criteria (we want to exclude these types)
        acc.excludes.push(deviceType);
      }

      return acc;
    },
    {
      includes: [],
      excludes: [],
    }
  );
};

/**
* @param {Object} - data as stored on the front-end:
* Ex 1: {
*   devices: {
*     android: ["tablet", "mobile"],
*     ios: ["tablet", "mobile"]
*   }
* }
*
* Ex 2: {
*   devices: {
*     android: ["mobile"],
*     ios: ["tablet"]
*   }
* }
* @return {string} - Lucene query format for devices:
* Ex 1: "+(+device_type:(tablet mobile) +os: (ios android))"
*
* Ex 2: (line breaks added for readability, would just be spaces in reality)
* +(
    (+device_type:(mobile) +os:(android))
    (+device_type:(tablet) +os:(ios))
  )
  -(
    (+device_type:(tablet) +os:(android))
    (+device_type:(mobile) +os:(ios))"
  )
*/
const convertDevicesToLucene = (devices: Devices): string => {
  /*
  {
    android: {
      includes: ["mobile"],
      excludes: ["tablet"]
    }
    ios: {
      includes: ["tablet"],
      excludes: ["mobile"]
    }
  }
  */
  const groupedByOs = DEVICE_OSS.reduce((acc, os) => {
    acc[os] = sortDeviceTypesByIncludedExcluded(devices[os]);
    return acc;
  }, {});

  /*
  [
  "+(
      (
        +device_type:(tablet)
        +os:(ios)
      )
      (
      +device_type:(mobile)
      +os:(android)
      )
    )"
  ]
  */
  const queries = [];

  if (groupedByOs.ios.includes.length || groupedByOs.android.includes.length) {
    const includeQueries = [];

    if (groupedByOs.ios.includes.length) {
      includeQueries.push(`(+device_type:(${groupedByOs.ios.includes.join(' ')}) +os:(ios))`);
    }

    if (groupedByOs.android.includes.length) {
      includeQueries.push(`(+device_type:(${groupedByOs.android.includes.join(' ')}) +os:(android))`);
    }

    queries.push(`+(${includeQueries.join(' ')})`);
  }

  if (groupedByOs.ios.excludes.length || groupedByOs.android.excludes.length) {
    const excludeQueries = [];

    if (groupedByOs.ios.excludes.length) {
      excludeQueries.push(`(+device_type:(${groupedByOs.ios.excludes.join(' ')}) +os:(ios))`);
    }

    if (groupedByOs.android.excludes.length) {
      excludeQueries.push(`(+device_type:(${groupedByOs.android.excludes.join(' ')}) +os:(android))`);
    }

    queries.push(`-(${excludeQueries.join(' ')})`);
  }

  if (queries && queries.length) {
    return queries.join(' ');
  }

  return '';
};

/**
 * Convert legacy lucene query to devices
 * This has only 'os' part of it. Which means all devices for the OS
 * should be included
 * @param  {string} query lucene query
 * @return {Object}       devices
 */
const convertLegacyLuceneToDevices = (query: string): Devices => {
  const devices = {};
  const osRegex = /\+\(?os:\(([^\)]*)\)\)?/g;
  const osRegexShort = /\+\(?os:(\w+)\)?/g;
  let osMatch = osRegex.exec(query);

  if (!osMatch) {
    osMatch = osRegexShort.exec(query);
  }

  if (osMatch !== null && osMatch.length >= 2) {
    const osList = osMatch[1].split(' ');
    osList.forEach((os) => (devices[os] = DEVICE_TYPES));
  }

  return devices;
};

const convertLuceneToDevices = (query: string): Devices => {
  const devices = {};
  // extract device matchers
  // +(+device_type:(tablet) +os:(android))
  const deviceRegex = /([\+])\((\((\+device_type:\(([^\)]*)\))[^\)]*\){2}[ ]?)+/g;
  const devicePartRegex = /\(\+device_type:\(([^\)]*)\) \+os:\(([^\)]*)\){2}/g;
  const deviceMatcher = deviceRegex.exec(query);

  if (!deviceMatcher) {
    return convertLegacyLuceneToDevices(query);
  }

  let deviceMatchPart = [];

  while ((deviceMatchPart = devicePartRegex.exec(deviceMatcher[0]))) {
    const deviceTypes = deviceMatchPart[1].split(' ');
    devices[deviceMatchPart[2]] = deviceTypes.length ? deviceTypes : DEVICE_TYPES;
  }

  return devices;
};

export {
  getEditorBodyStyle,
  getTotalHeight,
  getDevicesText,
  sortDeviceTypesByIncludedExcluded,
  convertDevicesToLucene,
  convertLuceneToDevices,
};
